import { Spacing } from '@persona-id/design-tokens';
import { MonitorIcon, PhoneMobileIcon } from '@persona-id/design-tokens/icons';
import { isEqual } from 'lodash/fp';
import {
  ChangeEventHandler,
  FocusEventHandler,
  KeyboardEventHandler,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import { getScreenDimensionsLiveUpdated as getPreviewScreenDimensionsLiveUpdated } from 'dashboard/apps/InquiryTemplateEditor/InquiryPreview/selectors/screenDimensions';
import { DEFAULT_SCREEN_DIMENSIONS } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/constants';
import { getScreenDimensionsLiveUpdated as getEditorScreenDimensionsLiveUpdated } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/selectors/screenDimensions';
import { ScreenDimensions } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/types';
import { RootState } from 'dashboard/redux/store';
import Button from 'design-system/components/Button';
import inputMixin from 'design-system/mixins/input';
import { paragraphMixins } from 'design-system/mixins/text';

// 1920:1080 aspect ratio scaled down to fit screen editor
export const DEFAULT_DESKTOP_DIMENSIONS = {
  height: 810,
  width: 1440,
};

const FakeInput = styled.div`
  ${inputMixin};
  height: 32px;
  overflow: hidden;
  padding: ${Spacing[1]} ${Spacing[2]};
`;

const StyledInput = styled.input`
  ${paragraphMixins.lg};
  width: 100px;
`;

const InputContainer = styled.span`
  display: inline-flex;
  gap: ${Spacing[1]};
  align-items: flex-end;
`;

const ButtonContainer = styled.div`
  border-radius: ${({ theme }) => theme.card.borderRadius};
  overflow: hidden;
  gap: ${Spacing[1]};
  display: flex;
`;

const StyledButton = styled(Button).attrs<{
  isActive: boolean;
  variant: 'basic';
}>((props) => {
  if (props.isActive)
    return {
      style: {
        backgroundColor: props.theme.color.variants[props.variant].active,
      },
    };
})<{ isActive: boolean; variant: 'basic' }>`
  background-color: ${({ theme }) => theme.color.background.secondary};
  width: 50%;
  border-radius: 0;
  // button component by default adds a box shadow
  box-shadow: none !important;
`;

interface ScreenDimensionsInputProps {
  inSimulate?: boolean;
  handleScreenDimensionsChange: (screenDimensions: ScreenDimensions) => void;
}

const ScreenDimensionsInput = ({
  inSimulate,
  handleScreenDimensionsChange,
}: ScreenDimensionsInputProps) => {
  // Currently both the editor and the simulator effectively are tied together via local storage but
  // this may deviate in the future, so setting it up so that they are isolated.
  const screenDimensions = useSelector<RootState, ScreenDimensions>((state: RootState) =>
    inSimulate
      ? getPreviewScreenDimensionsLiveUpdated(state)
      : getEditorScreenDimensionsLiveUpdated(state),
  );

  const [width, setWidth] = useState<number>(screenDimensions.height);
  const [height, setHeight] = useState<number>(screenDimensions.width);
  const heightInputRef = useRef<HTMLInputElement>(null);
  const widthInputRef = useRef<HTMLInputElement>(null);

  /**
   * other components may update the redux store, so we need to update the local state to reflect that:
   * - the component that passes handleScreenDimensionsChange
   * - the editor toolbar component, reset button
   */
  useEffect(() => {
    setWidth(screenDimensions.width);
    setHeight(screenDimensions.height);
  }, [screenDimensions]);

  const handleHeightKeyDown: KeyboardEventHandler<HTMLInputElement> = (e) => {
    if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault();
      heightInputRef.current?.blur();
    }
  };

  const handleWidthKeyDown: KeyboardEventHandler<HTMLInputElement> = (e) => {
    if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault();
      widthInputRef.current?.blur();
    }
  };

  const handleWidthChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    setWidth(parseInt(e.target.value, 10));
  };

  const handleHeightChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    setHeight(parseInt(e.target.value, 10));
  };

  const handleBlur: FocusEventHandler<HTMLInputElement> = () => {
    handleScreenDimensionsChange({ width, height });
  };

  return (
    <>
      <ButtonContainer>
        <StyledButton
          onClick={() => handleScreenDimensionsChange(DEFAULT_SCREEN_DIMENSIONS)}
          id='mobile-screen-dimensions--button'
          data-test='mobile-screen-dimensions--button'
          variant='basic'
          isActive={isEqual(screenDimensions, DEFAULT_SCREEN_DIMENSIONS)}
        >
          <PhoneMobileIcon />
        </StyledButton>
        <StyledButton
          onClick={() => handleScreenDimensionsChange(DEFAULT_DESKTOP_DIMENSIONS)}
          id='desktop-screen-dimensions--button'
          data-test='desktop-screen-dimensions--button'
          variant='basic'
          isActive={isEqual(screenDimensions, DEFAULT_DESKTOP_DIMENSIONS)}
        >
          <MonitorIcon />
        </StyledButton>
      </ButtonContainer>

      <InputContainer>
        <div>
          Width
          <FakeInput>
            <StyledInput
              type='number'
              ref={widthInputRef}
              onKeyDown={handleWidthKeyDown}
              onChange={handleWidthChange}
              onBlur={handleBlur}
              value={width}
            />
            px
          </FakeInput>
        </div>
        <div>
          Height
          <FakeInput>
            <StyledInput
              type='number'
              ref={heightInputRef}
              onKeyDown={handleHeightKeyDown}
              onChange={handleHeightChange}
              onBlur={handleBlur}
              value={height}
            />
            px
          </FakeInput>
        </div>
      </InputContainer>
    </>
  );
};

export default ScreenDimensionsInput;
